/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { defineMessages } from 'react-intl';
import { routeTable } from 'src/core/utils/routeTable';
import { Portal } from 'src/core/utils/routingUtils';
import { CapabilityType } from 'src/languageStudio/core/types';

export type SchemaType = 'Entities' | 'Intents';

const messages = defineMessages({
    a: { id: 'modules.modelGuidanceUtils.a', defaultMessage: 'a' },
    an: { id: 'modules.modelGuidanceUtils.an', defaultMessage: 'an' },
    class: { id: 'modules.modelGuidanceUtils.class', defaultMessage: 'class' },
    micro: { id: 'modules.modelGuidanceUtils.micro', defaultMessage: 'micro' },
    entity: { id: 'modules.modelGuidanceUtils.entity', defaultMessage: 'entity' },
    intent: { id: 'modules.modelGuidanceUtils.intent', defaultMessage: 'intent' },
    intents: { id: 'modules.modelGuidanceUtils.intents', defaultMessage: 'intents' },
    classes: { id: 'modules.modelGuidanceUtils.classes', defaultMessage: 'classes' },
    entities: { id: 'modules.modelGuidanceUtils.entities', defaultMessage: 'entities' },
    strictMicro: { id: 'modules.modelGuidanceUtils.strictMicro', defaultMessage: 'strict micro' },
    normalized: { id: 'modules.modelGuidanceUtils.normalized', defaultMessage: 'Normalized' },
    rawCount: { id: 'modules.modelGuidanceUtils.rawCount', defaultMessage: 'Raw Count' },
    capitalizedClass: { id: 'modules.modelGuidanceUtils.capitalizedClass', defaultMessage: 'Classes' },
    capitalizedIntent: { id: 'modules.modelGuidanceUtils.capitalizedIntent', defaultMessage: 'Intents' },
    capitalizedEntity: { id: 'modules.modelGuidanceUtils.capitalizedEntity', defaultMessage: 'Entities' },
    extractionModelType: { id: 'modules.modelGuidanceUtils.extractionModelType', defaultMessage: 'extraction' },
    charOverlapCount: { id: 'modules.modelGuidanceUtils.charOverlapCount', defaultMessage: 'Character Overlap Count' },
    classificationModelType: { id: 'modules.modelGuidanceUtils.classificationModelType', defaultMessage: 'classification' },
    normalizedCharOverlap: { id: 'modules.modelGuidanceUtils.normalizedCharOverlap', defaultMessage: 'Normalized Character Overlap' },
    formatDropDownCluIntentsToolTip: {
        id: 'modules.modelGuidanceUtils.formatDropDownCluIntentsToolTip',
        defaultMessage:
            'Change how the values in the matrix appear. Raw count is the actual number of utterances that have been predicted and labeled for a set of intents. Normalized value is the ratio, between 0 and 1, of the predicted and labeled utterances for a set of intents.'
    },
    formatDropDownCluEntitiesToolTip: {
        id: 'modules.modelGuidanceUtils.formatDropDownCluEntitiesToolTip',
        defaultMessage:
            'Change how the values in the matrix appear. Character overlap count is the actual number of spans that have been predicted and labeled for a set of entities. Normalized character overlap is the ratio, between 0 and 1, of the predicted and labeled spans for a set of entities.'
    },
    formatDropDownExtractionToolTip: {
        id: 'modules.modelGuidanceUtils.formatDropDownExtractionToolTip',
        defaultMessage:
            'Change how the values in the matrix appear. Raw count is the actual number of documents that have been predicted and labeled for an entity. Normalized value is the ratio, between 0 and 1, of the predicted and labeled documents for that entity out of all predicted and labeled documents.'
    },
    formatDropDownClassificationToolTip: {
        id: 'modules.modelGuidanceUtils.formatDropDownClassificationToolTip',
        defaultMessage:
            'Change how the values in the matrix appear. Raw count is the actual number of documents that have been predicted and labeled for certain class. Normalized value is the ratio, between 0 and 1, of the predicted and labeled documents for that class out of all predicted and labeled documents.'
    }
});

const getCapitalizedLabel = (pivotName: SchemaType | 'Classes', serviceName: Portal['serviceName']) => {
    if (pivotName === 'Entities' || serviceName === 'ner' || serviceName === 'customHealthcare') {
        return messages.capitalizedEntity;
    }

    if (pivotName === 'Intents') {
        return messages.capitalizedIntent;
    }

    return messages.capitalizedClass;
};

const getSmallLabel = (pivotName: SchemaType | 'Classes', serviceName: Portal['serviceName']) => {
    if (pivotName === 'Entities' || serviceName === 'ner' || serviceName === 'customHealthcare') {
        return messages.entities;
    }

    if (pivotName === 'Intents') {
        return messages.intents;
    }

    return messages.classes;
};

export const getStrictLabel = (pivotName: SchemaType | 'Classes', serviceName: Portal['serviceName']) => {
    if (pivotName === 'Entities' || serviceName === 'ner' || serviceName === 'customHealthcare') {
        return messages.strictMicro;
    }

    return messages.micro;
};

export const getIndefinite = (pivotName: SchemaType | 'Classes', serviceName: Portal['serviceName']) => {
    if (pivotName === 'Entities' || pivotName === 'Intents' || serviceName === 'ner' || serviceName === 'customHealthcare') {
        return messages.an;
    }

    return messages.a;
};

export const getSingleModelName = (pivotName: SchemaType, serviceName?: Portal['serviceName']) => {
    if (pivotName === 'Intents') {
        return messages.intent;
    }

    if (pivotName === 'Entities' || serviceName === 'ner' || serviceName === 'customHealthcare') {
        return messages.entity;
    }

    return messages.class;
};

export const handleGetLabel = (pivotName: SchemaType, serviceName: Portal['serviceName'], isCapital: boolean = false) =>
    isCapital ? getCapitalizedLabel(pivotName, serviceName) : getSmallLabel(pivotName, serviceName);

export const getModelTypeLabel = (pivotName: SchemaType, serviceName: Portal['serviceName']) => {
    if (pivotName === 'Entities' || serviceName === 'ner' || serviceName === 'customHealthcare') {
        return messages.extractionModelType;
    }

    return messages.classificationModelType;
};

export const getNormalizedFormatDropDownLabel = (pivotName: SchemaType, serviceName: Portal['serviceName']) => {
    if (pivotName === 'Entities' || serviceName === 'ner' || serviceName === 'customHealthcare') {
        return messages.normalizedCharOverlap;
    }

    return messages.normalized;
};

export const getRawCountFormatDropDownLabel = (pivotName: SchemaType, serviceName: Portal['serviceName']) => {
    if (pivotName === 'Entities' || serviceName === 'ner' || serviceName === 'customHealthcare') {
        return messages.charOverlapCount;
    }

    return messages.rawCount;
};

export const getFormatDropDownToolTipText = (serviceName: Portal['serviceName'], pivotName?: SchemaType) => {
    switch (serviceName) {
        case 'clu':
            return pivotName === 'Entities' ? messages.formatDropDownCluEntitiesToolTip : messages.formatDropDownCluIntentsToolTip;
        case 'classification':
            return messages.formatDropDownClassificationToolTip;
        default:
            return messages.formatDropDownExtractionToolTip;
    }
};

export const getRouteToTraining = (capabilityName: CapabilityType, projectId: string) => {
    switch (capabilityName) {
        case 'Conversation':
            return routeTable.clu.trainingPage(projectId);
        case 'Orchestration':
            return routeTable.orchestration.trainingPage(projectId);
        default:
            return routeTable.customText.trainingPage(projectId);
    }
};

export const getRouteToDataTaggingPage = (projectName: string, capabilityName: CapabilityType) => {
    switch (capabilityName) {
        case 'Conversation':
            return routeTable.clu.dataTaggingPage(projectName);
        case 'Orchestration':
        case 'Workflow':
            return routeTable.orchestration.dataTaggingPage(projectName);
        case 'CustomEntityRecognition':
        case 'CustomSingleLabelClassification':
        case 'CustomMultiLabelClassification':
            return routeTable.customText.dataTaggingPage(projectName, capabilityName);
        default:
            return null;
    }
};
